import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Save, XCircle, Info, Search } from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

export default function LeaveForm({
    onSave,
    onCancel,
    isSubmitting,
    initialData,
    apiError,
    employees = [],
    leaveTypes = [],
    canOverrideApproval = false
}) {
    const { userId } = useAuth();
    const [formData, setFormData] = useState({
        employeeId: '',
        izinTuruId: '',
        baslangicTarihi: '',
        bitisTarihi: '',
        aciklama: '',

        // Admin/İK: Onay akışını bypass ederek direkt onaylı kaydet
        onaysizKaydet: false,
        onaysizKaydetNotu: ''
    });

    // Sunucu taraflı personel arama (10.000+ personel ölçeklenebilirliği için)
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [employeeResults, setEmployeeResults] = useState([]);
    const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
    const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
    const [selectedEmployeeObj, setSelectedEmployeeObj] = useState(null);

    // Esnek türlerde: 1 = Gün, 2 = Saat
    const [selectedUsageUnit, setSelectedUsageUnit] = useState(1);

    // Saatlik izin için saat alanları
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('18:00');

    const [leaveDurationText, setLeaveDurationText] = useState('');

    // Kullanıcının talep ettiği izin süresini (dakika & gün cinsinden) takip ediyoruz.
    const [requestedMinutes, setRequestedMinutes] = useState(null);
    const [requestedDays, setRequestedDays] = useState(null);
    const [durationLoading, setDurationLoading] = useState(false);

    // Bakiye özet bilgileri
    const [balanceInfo, setBalanceInfo] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [balanceError, setBalanceError] = useState(null);

    // API hata metnini kullanıcıya gösterirken kullanacağımız versiyon
    const displayApiError = useMemo(() => {
        if (!apiError) return null;

        // Backend'den gelen 400 mesajlarını maskelersek (ör. her 400'ü 'çakışma' diye),
        // vekalet gibi farklı 400 senaryoları yanlış görünür.
        if (typeof apiError === 'string') return apiError;

        if (apiError?.message) return apiError.message;

        try {
            return JSON.stringify(apiError);
        } catch {
            return String(apiError);
        }
    }, [apiError]);

    // Backend çakışma mesajını yakalamak için (görsel olarak tarih alanlarının altında da gösterebiliriz)
    const isOverlapError =
        typeof displayApiError === 'string' &&
        displayApiError.toLowerCase().includes('bekleyen') &&
        displayApiError.toLowerCase().includes('izin talebi');

    const formatDateForInput = (dateInput) => {
        if (!dateInput) return '';
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (Number.isNaN(d.getTime())) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatTimeForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Sunucu taraflı personel arama (Employee/dropdown?search=...&limit=30)
    useEffect(() => {
        if (!employeeDropdownOpen && !employeeSearch.trim()) {
            return;
        }
        let isMounted = true;
        const timer = setTimeout(async () => {
            setEmployeeSearchLoading(true);
            try {
                const qs = `search=${encodeURIComponent(employeeSearch.trim())}&limit=30`;
                const data = await apiClient(`Employee/dropdown?${qs}`);
                if (isMounted) {
                    setEmployeeResults(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (isMounted) setEmployeeResults([]);
            } finally {
                if (isMounted) setEmployeeSearchLoading(false);
            }
        }, 250);
        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [employeeSearch, employeeDropdownOpen]);

    // Seçili personel ve izin türü objeleri
    const selectedEmployee = useMemo(() => {
        if (selectedEmployeeObj && String(selectedEmployeeObj.id) === String(formData.employeeId)) {
            return selectedEmployeeObj;
        }
        return (employees || []).find((e) => String(e.id) === String(formData.employeeId)) || null;
    }, [selectedEmployeeObj, employees, formData.employeeId]);

    const selectedLeaveType = (leaveTypes || []).find(
        (t) => String(t.id) === String(formData.izinTuruId)
    );


    // ---------------------------------------------------------------------
    // VEKALET (KENDİ İZNİNİ GİRERKEN)
    //
    // ÖNEMLİ: Vekil paneli "personel = kendisi" seçildi diye otomatik açılmaz.
    // Sadece backend "vekil zorunlu" hatası döndüğünde açılır.
    // Böylece vekil zorunluluğu izin türünün workflow'una bağlı kalır.
    // ---------------------------------------------------------------------

    const getEmployeeUserId = (emp) =>
        emp?.userId ?? emp?.kullaniciId ?? emp?.user?.id ?? emp?.user?.userId ?? null;

    const currentUserEmployeeId = useMemo(() => {
        if (selectedEmployee && String(getEmployeeUserId(selectedEmployee)) === String(userId)) {
            return selectedEmployee.id;
        }
        const me = (employees || []).find((e) => String(getEmployeeUserId(e)) === String(userId));
        return me ? me.id : null;
    }, [selectedEmployee, employees, userId]);

    const isSelfRequest = useMemo(() => {
        if (!formData.employeeId) return false;
        if (selectedEmployee && String(getEmployeeUserId(selectedEmployee)) === String(userId)) {
            return true;
        }
        if (!currentUserEmployeeId) return false;
        return String(formData.employeeId) === String(currentUserEmployeeId);
    }, [formData.employeeId, selectedEmployee, currentUserEmployeeId, userId]);

    // Backend "vekil zorunlu" hatasını yakalarsak paneli açacağız.
    const isDelegateRequiredError = useMemo(() => {
        if (!displayApiError || typeof displayApiError !== 'string') return false;
        const msg = displayApiError.toLowerCase();
        // farklı backend mesajlarını yakalamak için geniş bir eşleşme
        const hasDelegateWord = msg.includes('vekil');
        const hasApprovalWord = msg.includes('onay') || msg.includes('onaycı');
        const hasRequiredWord =
            msg.includes('zorun') ||
            msg.includes('gerek') ||
            msg.includes('tanımla') ||
            msg.includes('tanimla') ||
            msg.includes('seç') ||
            msg.includes('sec');
        return hasDelegateWord && hasApprovalWord && hasRequiredWord;
    }, [displayApiError]);

    const [delegatePanelOpen, setDelegatePanelOpen] = useState(false);

    // Self request değilse panel kapanır. Self request + delegate required error ise panel açılır.
    useEffect(() => {
        if (!isSelfRequest) {
            if (delegatePanelOpen) setDelegatePanelOpen(false);
            return;
        }

        if (isDelegateRequiredError) {
            setDelegatePanelOpen(true);
        }
    }, [isSelfRequest, isDelegateRequiredError, delegatePanelOpen]);

    const [delegateAvailable, setDelegateAvailable] = useState([]);
    const [delegateSelected, setDelegateSelected] = useState(new Set());
    const [delegateMineIds, setDelegateMineIds] = useState([]);
    const [delegateLoading, setDelegateLoading] = useState(false);
    const [delegateError, setDelegateError] = useState(null);

    const normalizeUserList = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.users)) return res.users;
        if (Array.isArray(res?.items)) return res.items;
        return [];
    };

    const resetDelegationsUi = () => {
        setDelegateError(null);
        setDelegateAvailable([]);
        setDelegateMineIds([]);
        setDelegateSelected(new Set());
        setDelegateLoading(false);
    };

    const toggleDelegate = (uid) => {
        setDelegateSelected((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
    };

    // Panel açıldığında uygun vekil listesini yükle (scope filtreli olması backend sorumluluğu)
    useEffect(() => {
        let isMounted = true;

        const loadDelegations = async () => {
            setDelegateLoading(true);
            setDelegateError(null);

            try {
                const [availRes, mineRes] = await Promise.all([
                    apiClient('delegations/available'),
                    apiClient('delegations/mine')
                ]);

                const avail = normalizeUserList(availRes);
                const mine = normalizeUserList(mineRes);
                const mineIds = (mine || [])
                    .map((x) => x.delegateUserId)
                    .filter((x) => x !== null && x !== undefined);

                if (!isMounted) return;

                setDelegateAvailable(avail);
                setDelegateMineIds(mineIds);
                setDelegateSelected(new Set(mineIds));
            } catch (e) {
                if (!isMounted) return;
                // Endpoint yetkisi yoksa / hata olursa panel kapansın demiyoruz;
                // sadece listeyi boş gösterip kullanıcıya mesaj veriyoruz.
                setDelegateAvailable([]);
                setDelegateMineIds([]);
                setDelegateSelected(new Set());
                setDelegateError('Vekil listesi alınamadı. Lütfen daha sonra tekrar deneyin.');
            } finally {
                if (isMounted) setDelegateLoading(false);
            }
        };

        if (delegatePanelOpen && isSelfRequest) {
            loadDelegations();
        } else {
            // panel kapalıysa, UI'yı temizle (özellikle employee değişince)
            resetDelegationsUi();
        }

        return () => {
            isMounted = false;
        };
    }, [delegatePanelOpen, isSelfRequest]);

    // ---------------------------------------------------------------------
    // KULLANIM BİRİMİ (GÜNLÜK / SAATLİK / ESNEK) LOGİĞİ
    // ---------------------------------------------------------------------

    // 0 veya 3 = Esnek, 1 = Günlük, 2 = Saatlik (backend IzinKullanimBirimi: Gun=1, Saat=2, Esnek=3)
    const normalizeKullanimBirimi = (val) => {
        if (val === null || val === undefined) return 1;
        if (typeof val === 'number') return val === 3 ? 0 : val;
        if (typeof val === 'string') {
            if (/^[0-9]+$/.test(val)) {
                const parsed = parseInt(val, 10);
                return parsed === 3 ? 0 : parsed;
            }
            const lower = val.toLowerCase();
            if (lower.includes('gun') || lower.includes('gün')) return 1;
            if (lower.includes('saat')) return 2;
            if (lower.includes('esnek')) return 0;
        }
        return 1;
    };

    const baseUnit = useMemo(
        () =>
            normalizeKullanimBirimi(
                selectedLeaveType ? selectedLeaveType.kullanimBirimi : null
            ),
        [selectedLeaveType]
    );

    // Esnek tür mü?
    const isEsnek = baseUnit === 0;

    // Günlük / Saatlik gerçek layout kararları:
    let isGunluk = false;
    let isSaatlik = false;

    if (isEsnek) {
        // Esnek türde kullanıcı ne seçerse ona göre
        isGunluk = selectedUsageUnit === 1;
        isSaatlik = selectedUsageUnit === 2;
    } else if (baseUnit === 2) {
        // 2 ise her zaman saatlik
        isSaatlik = true;
    } else {
        // 1 veya null / bilinmeyen değerleri HER ZAMAN günlük say
        isGunluk = true;
    }

    // ❗ Esnek tür için DEFAULT kullanım birimini
    // SADECE izin türü değiştiğinde belirle (kullanıcının seçimini ezme)
    useEffect(() => {
        if (!selectedLeaveType) return;

        if (baseUnit === 2) {
            // Net saatlik bir tür → 2
            setSelectedUsageUnit(2);
        } else {
            // Esnek veya günlük → başlangıçta 1 (günlük)
            setSelectedUsageUnit(1);
        }
    }, [selectedLeaveType?.id, baseUnit]);

    // ---------------------------------------------------------------------
    // BAKİYE MODU & NEGATİF BAKİYE
    // ---------------------------------------------------------------------

    const normalizeBakiyeModu = (val) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            if (/^[0-9]+$/.test(val)) return parseInt(val, 10);
            const lower = val.toLowerCase();
            if (lower.includes('yillik') || lower.includes('yıllık')) return 1;
            if (lower.includes('event')) return 2;
        }
        return 0;
    };

    const bakiyeModu = selectedLeaveType
        ? normalizeBakiyeModu(selectedLeaveType.bakiyeModu)
        : 0;

    // NEGATİF BAKİYE AYARLARI (izin türünden gelir)
    const canGoNegative = selectedLeaveType
        ? !!selectedLeaveType.negatifBakiyeKullanilsinMi
        : false;

    const negativeLimitDays =
        selectedLeaveType && selectedLeaveType.negatifBakiyeGunLimiti != null
            ? Number(selectedLeaveType.negatifBakiyeGunLimiti)
            : null;

    const isBalanceTracked = bakiyeModu !== 0; // 0 = Bakiyesiz (bakiye takibi yok)

    // Günlük çalışma süresi (dinamik: izin türü -> hesaplanan vardiya süresi -> bakiye özeti), dakika cinsinden
    const dailyMinutes = useMemo(() => {
        if (selectedLeaveType && selectedLeaveType.varsayilanGunSaat != null) {
            const raw = selectedLeaveType.varsayilanGunSaat;
            const num = typeof raw === 'string' ? Number(raw.replace(',', '.')) : Number(raw);
            if (!Number.isNaN(num) && num > 0) {
                return Math.round(num * 60);
            }
        }
        if (typeof requestedMinutes === 'number' && requestedMinutes > 0 && typeof requestedDays === 'number' && requestedDays > 0) {
            return Math.round(requestedMinutes / requestedDays);
        }
        if (
            typeof balanceInfo?.toplamHakDakika === 'number' &&
            balanceInfo.toplamHakDakika > 0 &&
            typeof balanceInfo?.gunBazliToplamHak === 'number' &&
            balanceInfo.gunBazliToplamHak > 0
        ) {
            return Math.round(balanceInfo.toplamHakDakika / balanceInfo.gunBazliToplamHak);
        }
        if (
            typeof balanceInfo?.kalanHakDakika === 'number' &&
            balanceInfo.kalanHakDakika !== 0 &&
            typeof balanceInfo?.gunBazliKalanHak === 'number' &&
            balanceInfo.gunBazliKalanHak !== 0
        ) {
            return Math.round(Math.abs(balanceInfo.kalanHakDakika / balanceInfo.gunBazliKalanHak));
        }
        return 480;
    }, [selectedLeaveType, requestedMinutes, requestedDays, balanceInfo]);

    const negativeLimitMinutes =
        negativeLimitDays != null && !Number.isNaN(negativeLimitDays) && dailyMinutes
            ? negativeLimitDays * dailyMinutes
            : null;

    // ---------------------------------------------------------------------
    // INITIAL DATA
    // ---------------------------------------------------------------------

    useEffect(() => {
        if (initialData) {
            setFormData({
                employeeId: initialData.employeeId
                    ? String(initialData.employeeId)
                    : '',
                izinTuruId: initialData.izinTuruId
                    ? String(initialData.izinTuruId)
                    : '',
                baslangicTarihi: formatDateForInput(initialData.baslangicTarihi),
                bitisTarihi: formatDateForInput(initialData.bitisTarihi),
                aciklama: initialData.aciklama || '',
                onaysizKaydet: false,
                onaysizKaydetNotu: ''
            });

            if (initialData.employeeId) {
                const existingEmp = (employees || []).find(
                    (e) => String(e.id) === String(initialData.employeeId)
                );
                setSelectedEmployeeObj(
                    existingEmp || {
                        id: initialData.employeeId,
                        fullName: initialData.employeeFullName || `ID: ${initialData.employeeId}`
                    }
                );
            } else {
                setSelectedEmployeeObj(null);
            }

            if (initialData.startDateTime) {
                setStartTime(formatTimeForInput(initialData.startDateTime));
            }
            if (initialData.endDateTime) {
                setEndTime(formatTimeForInput(initialData.endDateTime));
            }
        } else {
            const today = formatDateForInput(new Date());
            setFormData({
                employeeId: '',
                izinTuruId: '',
                baslangicTarihi: today,
                bitisTarihi: today,
                aciklama: '',
                onaysizKaydet: false,
                onaysizKaydetNotu: ''
            });
            setSelectedEmployeeObj(null);
            setEmployeeSearch('');
            setStartTime('09:00');
            setEndTime('18:00');
        }
    }, [initialData]);

    // ---------------------------------------------------------------------
    // SÜRE ÖNİZLEME (talep edilen izin süresi)
    // ---------------------------------------------------------------------

    const calcDurationPreview = async () => {
        if (!formData.baslangicTarihi || !formData.izinTuruId || !formData.employeeId) {
            setLeaveDurationText('');
            setRequestedMinutes(null);
            setRequestedDays(null);
            return;
        }

        let startDateTime = formData.baslangicTarihi;
        let endDateTime = formData.bitisTarihi || formData.baslangicTarihi;
        let sTime = null;
        let eTime = null;

        if (isSaatlik) {
            sTime = startTime;
            eTime = endTime;
            const [sh, sm] = startTime.split(':').map((x) => parseInt(x || '0', 10));
            const [eh, em] = endTime.split(':').map((x) => parseInt(x || '0', 10));

            const start = new Date(`${startDateTime}T${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}:00`);
            const end = new Date(`${startDateTime}T${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}:00`);

            if (end <= start) {
                setLeaveDurationText('Bitiş saati başlangıç saatinden büyük olmalıdır.');
                setRequestedMinutes(null);
                setRequestedDays(null);
                return;
            }
        } else {
            const start = new Date(startDateTime);
            const end = new Date(endDateTime);
            if (end < start) {
                setLeaveDurationText('');
                setRequestedMinutes(null);
                setRequestedDays(null);
                return;
            }
        }

        setDurationLoading(true);
        try {
            const payload = {
                employeeId: Number(formData.employeeId),
                izinTuruId: Number(formData.izinTuruId),
                baslangicTarihi: new Date(startDateTime).toISOString(),
                bitisTarihi: new Date(endDateTime).toISOString(),
                startTime: isSaatlik ? sTime : null,
                endTime: isSaatlik ? eTime : null,
                kullanimBirimi: isSaatlik ? 2 : 1
            };

            const response = await apiClient('izin/calculate-duration', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (response.warningMessage) {
                setLeaveDurationText(response.warningMessage);
            } else {
                setLeaveDurationText(response.durationText);
            }
            
            setRequestedMinutes(response.netMinutes);
            setRequestedDays(response.netDays);

        } catch (err) {
            console.error('Süre hesaplama hatası:', err);
            setLeaveDurationText('Süre hesaplanamadı.');
            setRequestedMinutes(null);
            setRequestedDays(null);
        } finally {
            setDurationLoading(false);
        }
    };

    useEffect(() => {
        calcDurationPreview();
    }, [
        formData.baslangicTarihi,
        formData.bitisTarihi,
        startTime,
        endTime,
        isSaatlik,
        formData.employeeId,
        formData.izinTuruId
    ]);

    // ---------------------------------------------------------------------
    // BAKİYE ÖZETİNİ ÇEK
    // ---------------------------------------------------------------------

    useEffect(() => {
        setBalanceError(null);

        if (!formData.employeeId || !formData.izinTuruId || !selectedLeaveType) {
            setBalanceLoading(false);
            return;
        }

        const localBakiyeModu = normalizeBakiyeModu(selectedLeaveType.bakiyeModu);
        if (localBakiyeModu === 0) {
            // Bakiyesiz mod: backend'den bakiye çağırmaya gerek yok
            setBalanceLoading(false);
            return;
        }

        let isCancelled = false;

        const fetchBalance = async () => {
            try {
                setBalanceLoading(true);
                const qs = `employeeId=${formData.employeeId}&izinTuruId=${formData.izinTuruId}`;
                const data = await apiClient(`izin-bakiyeleri/summary?${qs}`);

                if (!isCancelled) {
                    setBalanceInfo(data);
                }
            } catch (err) {
                if (isCancelled) return;

                if (err && err.status === 404) {
                    setBalanceInfo(null);
                    setBalanceError(null);
                } else {
                    console.error('İzin bakiyesi okunamadı:', err);
                    const msg =
                        (err && (err.userFriendlyMessage || err.message)) ||
                        'İzin bakiyesi bilgisi alınırken bir hata oluştu.';
                    setBalanceError(msg);
                }
            } finally {
                if (!isCancelled) {
                    setBalanceLoading(false);
                }
            }
        };

        fetchBalance();

        return () => {
            isCancelled = true;
        };
    }, [formData.employeeId, formData.izinTuruId, selectedLeaveType]);

    // ---------------------------------------------------------------------
    // HANDLERS
    // ---------------------------------------------------------------------

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked ? value : '' }));
            return;
        }

        if (name === 'baslangicTarihi') {
            setFormData((prev) => {
                let newEnd = prev.bitisTarihi || value;
                if (newEnd < value) newEnd = value;
                return {
                    ...prev,
                    baslangicTarihi: value,
                    bitisTarihi: newEnd
                };
            });
        } else if (name === 'bitisTarihi') {
            setFormData((prev) => {
                let newEnd = value || prev.baslangicTarihi;
                if (newEnd < (prev.baslangicTarihi || value)) {
                    newEnd = prev.baslangicTarihi || value;
                }
                return {
                    ...prev,
                    bitisTarihi: newEnd
                };
            });
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.employeeId || !formData.izinTuruId) {
            return;
        }

        const startDate = new Date(formData.baslangicTarihi);
        let endDate = new Date(formData.bitisTarihi || formData.baslangicTarihi);

        if (endDate < startDate) {
            endDate = startDate;
        }

        let startDateTime = startDate;
        let endDateTime = endDate;

        if (isSaatlik) {
            const [sh, sm] = startTime.split(':').map((x) => parseInt(x || '0', 10));
            const [eh, em] = endTime.split(':').map((x) => parseInt(x || '0', 10));

            startDateTime = new Date(
                `${formData.baslangicTarihi}T${String(sh).padStart(2, '0')}:${String(sm).padStart(
                    2,
                    '0'
                )}:00`
            );
            endDateTime = new Date(
                `${formData.baslangicTarihi}T${String(eh).padStart(2, '0')}:${String(em).padStart(
                    2,
                    '0'
                )}:00`
            );

            if (endDateTime <= startDateTime) {
                return;
            }
        }
        // VEKALET (Option-1): delegasyonu ayrı endpoint'e yazmıyoruz.
        // Seçilen vekiller izin payload'ı ile birlikte backend'e gönderilecek.
        let selectedDelegateIds = null;

        if (isSelfRequest && delegatePanelOpen) {
            if (delegateSelected.size === 0) {
                setDelegateError('Kendi izin talebinizi oluşturabilmek için en az 1 vekil onaycı seçmelisiniz.');
                return;
            }

            selectedDelegateIds = Array.from(delegateSelected);
            // UI tarafında seçimi hatırlamak için (isteğe bağlı)
            setDelegateMineIds(selectedDelegateIds);
        }

        let talepKullanimBirimi
            = 1;
        if (isEsnek) {
            talepKullanimBirimi = selectedUsageUnit;
        } else if (isSaatlik) {
            talepKullanimBirimi = 2;
        } else {
            talepKullanimBirimi = 1;
        }

        const payload = {
            employeeId: Number(formData.employeeId),
            izinTuruId: Number(formData.izinTuruId),
            delegateUserIds: selectedDelegateIds && selectedDelegateIds.length ? selectedDelegateIds : null,
            baslangicTarihi: startDateTime.toISOString(),
            bitisTarihi: endDateTime.toISOString(),
            startDateTime: isSaatlik ? startDateTime.toISOString() : null,
            endDateTime: isSaatlik ? endDateTime.toISOString() : null,
            aciklama: formData.aciklama || null,
            talepKullanimBirimi,

            ...(canOverrideApproval && !initialData && formData.onaysizKaydet
                ? {
                    onaysizKaydet: true,
                    onaysizKaydetNotu: formData.onaysizKaydetNotu || null
                }
                : {})
        };

        onSave(payload);
    };

    // ---------------------------------------------------------------------
    // BAKİYE KONTROLÜ (BUTONU KİLİTLEME)
    // ---------------------------------------------------------------------

    // Backend özetinden gelen değerler
    const kalanGun =
        typeof balanceInfo?.gunBazliKalanHak === 'number'
            ? balanceInfo.gunBazliKalanHak
            : null;

    const kalanDakika =
        typeof balanceInfo?.kalanHakDakika === 'number'
            ? balanceInfo.kalanHakDakika
            : null;

    const toplamHakDakika =
        typeof balanceInfo?.toplamHakDakika === 'number'
            ? balanceInfo.toplamHakDakika
            : null;

    const kullanilanHakDakika =
        typeof balanceInfo?.kullanilanHakDakika === 'number'
            ? balanceInfo.kullanilanHakDakika
            : null;

    const bekleyenTalepDakika =
        typeof balanceInfo?.bekleyenTalepDakika === 'number'
            ? balanceInfo.bekleyenTalepDakika
            : 0;

    const bekleyenTalepGun =
        typeof balanceInfo?.bekleyenTalepGun === 'number'
            ? balanceInfo.bekleyenTalepGun
            : null;

    const talepEdilebilirDakika =
        typeof balanceInfo?.talepEdilebilirDakika === 'number'
            ? balanceInfo.talepEdilebilirDakika
            : null;

    const talepEdilebilirGun =
        typeof balanceInfo?.talepEdilebilirGun === 'number'
            ? balanceInfo.talepEdilebilirGun
            : null;

    const hasAnyBalanceRecord = balanceInfo != null;

    // Kullanıcı gerçekten bir süre seçti mi?
    const hasRequestedUsage =
        typeof requestedMinutes === 'number' && requestedMinutes > 0;

    // Talep edilen gün sayısını hesapla (gün bazlı UI için hala lazım olabilir)
    let requestedDaysForBalance = 0;
    if (typeof requestedMinutes === 'number' && requestedMinutes > 0 && dailyMinutes > 0) {
        requestedDaysForBalance = requestedMinutes / dailyMinutes;
    } else if (typeof requestedDays === 'number' && requestedDays > 0) {
        requestedDaysForBalance = requestedDays;
    }

    // Pozitif gün hesabı (sadece görsel amaçlı - bakiye kartı rengi)
    let positiveDays = 0;

    if (typeof kalanGun === 'number' && !Number.isNaN(kalanGun)) {
        positiveDays = kalanGun;
    } else if (
        typeof kalanDakika === 'number' &&
        !Number.isNaN(kalanDakika) &&
        dailyMinutes > 0
    ) {
        positiveDays = kalanDakika / dailyMinutes;
    }

    const hasPositiveBalance = positiveDays > 0;

    // Negatif limit (gün bazında - bilgi amaçlı)
    const negativeLimitDaysSafe =
        canGoNegative &&
            typeof negativeLimitDays === 'number' &&
            !Number.isNaN(negativeLimitDays) &&
            negativeLimitDays > 0
            ? negativeLimitDays
            : 0;

    // Toplam kullanılabilir teorik gün (pozitif + negatif)
    const totalAllowedDays = positiveDays + negativeLimitDaysSafe;

    // Bu talep onaylandığında toplam kullanım (dakika):
    const totalAfterRequestMinutes =
        (kullanilanHakDakika || 0) + bekleyenTalepDakika + (hasRequestedUsage ? requestedMinutes : 0);

    // Bu talep ile negatif bakiyeye düşecek mi?
    const isUsingNegative =
        isBalanceTracked &&
        hasRequestedUsage &&
        toplamHakDakika != null &&
        talepEdilebilirDakika != null &&
        totalAfterRequestMinutes > toplamHakDakika &&
        requestedMinutes <= talepEdilebilirDakika;

    // Limit tamamen aşıldı mı? (butonu kilitleme kriteri)
    const shouldBlockByBalance =
        isBalanceTracked &&
        hasRequestedUsage &&
        talepEdilebilirDakika != null &&
        requestedMinutes > talepEdilebilirDakika;

    // ---------------------------------------------------------------------
    // PERSONEL GÖRÜNTÜ İSMİ
    // ---------------------------------------------------------------------

    const getEmployeeDisplayName = (emp) => {
        if (!emp) return '';
        if (emp.fullName) return emp.fullName;
        if (emp.adSoyad) return emp.adSoyad;

        const parts = [
            emp.firstName,
            emp.lastName,
            emp.ad,
            emp.soyad,
            emp.name,
            emp.surname
        ].filter(Boolean);

        if (parts.length > 0) return parts.join(' ');
        return `ID: ${emp.id}`;
    };

    // ---------------------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------------------

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Genel hata mesajı (API kaynaklı) */}
            {displayApiError && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-start">
                    <XCircle className="w-5 h-5 mr-2 mt-0.5" />
                    <div>
                        <p className="font-semibold">İşlem Başarısız</p>
                        <p className="text-sm">{displayApiError}</p>
                    </div>
                </div>
            )}

            {/* Personel (Sunucu Taraflı Arama Combobox) */}
            <div>
                <label
                    htmlFor="employeeSearchInput"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                >
                    Personel
                </label>

                {formData.employeeId && selectedEmployee ? (
                    <div className="mt-1 flex items-center justify-between px-3 py-2 rounded-lg border border-blue-300 bg-blue-50/70 dark:bg-blue-900/30 dark:border-blue-700 text-sm">
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {getEmployeeDisplayName(selectedEmployee)}
                            </span>
                            {(selectedEmployee.departmentName || selectedEmployee.identityNumber) && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {[selectedEmployee.departmentName, selectedEmployee.identityNumber]
                                        .filter(Boolean)
                                        .join(' • ')}
                                </span>
                            )}
                        </div>
                        {!isSubmitting && (
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData((prev) => ({ ...prev, employeeId: '' }));
                                    setSelectedEmployeeObj(null);
                                    setEmployeeSearch('');
                                    setEmployeeDropdownOpen(true);
                                }}
                                className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                            >
                                <XCircle className="w-4 h-4" />
                                Değiştir
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="relative mt-1">
                        <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                                id="employeeSearchInput"
                                type="text"
                                value={employeeSearch}
                                onFocus={() => setEmployeeDropdownOpen(true)}
                                onChange={(e) => {
                                    setEmployeeSearch(e.target.value);
                                    setEmployeeDropdownOpen(true);
                                }}
                                placeholder="Personel adı, soyadı veya T.C. kimlik no ile arayın..."
                                className="pl-9 pr-8 py-2 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                disabled={isSubmitting}
                                autoComplete="off"
                            />
                            {employeeSearchLoading && (
                                <Loader2 className="w-4 h-4 text-blue-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
                            )}
                        </div>

                        {employeeDropdownOpen && (
                            <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                                {employeeSearchLoading ? (
                                    <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                        Personeller aranıyor...
                                    </div>
                                ) : employeeResults.length === 0 ? (
                                    <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                                        Eşleşen personel bulunamadı.
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {employeeResults.map((emp) => (
                                            <li
                                                key={emp.id}
                                                onClick={() => {
                                                    setSelectedEmployeeObj(emp);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        employeeId: String(emp.id)
                                                    }));
                                                    setEmployeeDropdownOpen(false);
                                                }}
                                                className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                            >
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {getEmployeeDisplayName(emp)}
                                                </div>
                                                {(emp.departmentName || emp.identityNumber) && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {[emp.departmentName, emp.identityNumber]
                                                            .filter(Boolean)
                                                            .join(' • ')}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}


                {/* Vekil uyarısı (Kendi iznini giren onaycı kullanıcılar için) */}
                {isSelfRequest && delegatePanelOpen && (
                    <div className="p-3 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800">
                        <div className="flex items-start">
                            <Info className="w-5 h-5 mr-2 mt-0.5 text-indigo-600" />
                            <div className="flex-1">
                                <p className="font-semibold">Vekil Onaycı Gerekli</p>
                                <p className="text-sm mt-1">
                                    Siz izin onay sürecinde <span className="font-semibold">onaycı</span> konumundasınız.
                                    Kendi talebinizi onaylayamayacağınız için, talebinizin akışta ilerleyebilmesi adına
                                    en az 1 vekil onaycı seçmelisiniz.
                                </p>

                                {delegateLoading ? (
                                    <div className="mt-2 flex items-center text-sm">
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Vekil listesi yükleniyor...
                                    </div>
                                ) : (
                                    <>
                                        {delegateError && (
                                            <div className="mt-2 text-sm text-red-700">{delegateError}</div>
                                        )}

                                        {(delegateAvailable || []).length === 0 ? (
                                            <div className="mt-2 text-sm text-indigo-700">
                                                Uygun vekil bulunamadı. (Veri görme kapsamınıza uygun bir personel tanımlayın.)
                                            </div>
                                        ) : (
                                            <div className="mt-3">
                                                <div className="text-sm font-semibold mb-1">Vekil Onaycı(lar)</div>
                                                <div className="max-h-44 overflow-auto rounded-md border border-indigo-200 bg-white">
                                                    <ul className="divide-y divide-indigo-100">
                                                        {delegateAvailable.map((u) => {
                                                            const id = u.userId ?? u.id;
                                                            const checked = delegateSelected.has(id);
                                                            return (
                                                                <li key={id} className="flex items-center justify-between p-2">
                                                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={checked}
                                                                            onChange={() => toggleDelegate(id)}
                                                                            className="w-4 h-4"
                                                                            disabled={isSubmitting}
                                                                        />
                                                                        <div className="flex flex-col">
                                                                            <span className="text-sm font-semibold text-gray-900">
                                                                                {u.displayName || u.username || 'Kullanıcı'}
                                                                            </span>
                                                                            {u.username && (
                                                                                <span className="text-xs text-gray-500">{u.username}</span>
                                                                            )}
                                                                        </div>
                                                                    </label>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                                <div className="mt-1 text-xs text-indigo-700">
                                                    Not: Vekiller, veri görme kapsamınıza uygun kişiler arasından listelenir.
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* İzin Türü */}
            <div>
                <label
                    htmlFor="izinTuruId"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                >
                    İzin Türü
                </label>
                <select
                    id="izinTuruId"
                    name="izinTuruId"
                    value={formData.izinTuruId}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    disabled={isSubmitting}
                >
                    <option value="">İzin türü seçin...</option>
                    {leaveTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.kod} - {type.ad}
                        </option>
                    ))}
                </select>

                {/* İzin Bakiye Özeti */}
                {selectedLeaveType && (
                    <div className="mt-2">
                        {isBalanceTracked ? (
                            <>
                                {balanceLoading && (
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                        <span>İzin bakiyesi yükleniyor...</span>
                                    </div>
                                )}
                                {!balanceLoading && balanceError && (
                                    <div className="mt-1 flex items-start text-xs text-red-600 dark:text-red-400">
                                        <XCircle className="w-4 h-4 mr-1 mt-[1px]" />
                                        <span>{balanceError}</span>
                                    </div>
                                )}
                                {!balanceLoading && !balanceError && (
                                    <>
                                        {hasAnyBalanceRecord ? (
                                            <div
                                                className={`mt-2 text-xs rounded-md border px-3 py-2 ${hasPositiveBalance
                                                    ? 'border-green-300 bg-green-50 text-green-700 dark:bg-green-900/20 dark:border-green-500'
                                                    : canGoNegative
                                                        ? 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:border-amber-500'
                                                        : 'border-red-300 bg-red-50 text-red-700 dark:bg-red-900/20 dark:border-red-500'
                                                    }`}
                                            >
                                                <div className="flex items-start">
                                                    <Info className="w-4 h-4 mr-2 mt-[2px]" />
                                                    <div>
                                                        <div className="font-semibold mb-1">
                                                            İzin Bakiye Özeti
                                                        </div>
                                                        <div className="space-y-0.5">
                                                            <div>
                                                                <span className="font-medium">
                                                                    Toplam Hak:
                                                                </span>{' '}
                                                                {balanceInfo.gunBazliToplamHak?.toLocaleString(
                                                                    'tr-TR'
                                                                )}{' '}
                                                                gün
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">
                                                                    Kullanılan:
                                                                </span>{' '}
                                                                {balanceInfo.gunBazliKullanilanHak?.toLocaleString(
                                                                    'tr-TR'
                                                                )}{' '}
                                                                gün
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">
                                                                    Kalan:
                                                                </span>{' '}
                                                                {typeof kalanGun === 'number'
                                                                    ? `${kalanGun.toLocaleString(
                                                                        'tr-TR'
                                                                    )} gün`
                                                                    : '–'}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">
                                                                    Bekleyen Talepler:
                                                                </span>{' '}
                                                                {typeof bekleyenTalepGun ===
                                                                    'number'
                                                                    ? `${bekleyenTalepGun.toLocaleString(
                                                                        'tr-TR'
                                                                    )} gün`
                                                                    : '–'}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">
                                                                    Talep Edilebilir:
                                                                </span>{' '}
                                                                {typeof talepEdilebilirGun ===
                                                                    'number'
                                                                    ? `${talepEdilebilirGun.toLocaleString(
                                                                        'tr-TR'
                                                                    )} gün`
                                                                    : '–'}
                                                            </div>
                                                            {canGoNegative &&
                                                                negativeLimitDays != null &&
                                                                !Number.isNaN(
                                                                    negativeLimitDays
                                                                ) && (
                                                                    <div>
                                                                        <span className="font-medium">
                                                                            Negatif Limit:
                                                                        </span>{' '}
                                                                        {negativeLimitDays} güne
                                                                        kadar negatife düşebilir.
                                                                    </div>
                                                                )}
                                                            {!hasPositiveBalance &&
                                                                !canGoNegative && (
                                                                    <div className="mt-1">
                                                                        Bu izin türünde
                                                                        kullanılabilir pozitif
                                                                        bakiye bulunmuyor ve
                                                                        negatif bakiyeye izin
                                                                        verilmemektedir.
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-xs rounded-md border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 px-3 py-2">
                                                Bu izin türü için tanımlı aktif bakiye kaydı
                                                bulunamadı.
                                                {!canGoNegative && isBalanceTracked && (
                                                    <span className="block mt-1">
                                                        Negatif bakiyeye izin verilmediğinden bu
                                                        izin türüyle talep onayı verilemez.
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="mt-2 text-xs rounded-md border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 px-3 py-2">
                                Bu izin türü için bakiye takibi yapılmamaktadır.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Tarih ve saat alanları */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Başlangıç Tarihi */}
                <div>
                    <label
                        htmlFor="baslangicTarihi"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                    >
                        Başlangıç Tarihi
                    </label>
                    <input
                        type="date"
                        id="baslangicTarihi"
                        name="baslangicTarihi"
                        value={formData.baslangicTarihi}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        disabled={isSubmitting}
                    />
                </div>

                {/* Bitiş Tarihi (saatlikte gizli) */}
                {!isSaatlik && (
                    <div>
                        <label
                            htmlFor="bitisTarihi"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                        >
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            id="bitisTarihi"
                            name="bitisTarihi"
                            value={formData.bitisTarihi}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            disabled={isSubmitting}
                        />
                    </div>
                )}

                {/* Saatlik izin için saat alanları */}
                {isSaatlik && (
                    <>
                        <div>
                            <label
                                htmlFor="startTime"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                            >
                                Başlangıç Saati
                            </label>
                            <input
                                type="time"
                                id="startTime"
                                name="startTime"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="endTime"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                            >
                                Bitiş Saati
                            </label>
                            <input
                                type="time"
                                id="endTime"
                                name="endTime"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                disabled={isSubmitting}
                            />
                        </div>
                    </>
                )}
            </div>

            {isOverlapError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Seçtiğiniz tarih/saat aralığı, mevcut bekleyen veya onaylanmış başka
                    bir izinle çakışıyor. Lütfen tarih/saat aralığını değiştirin.
                </p>
            )}

            {/* Esnek türler için kullanım birimi seçimi */}
            {isEsnek && (
                <div>
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Kullanım Birimi
                    </span>
                    <div className="flex items-center space-x-4">
                        <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-200">
                            <input
                                type="radio"
                                name="usageUnit"
                                value="1"
                                checked={selectedUsageUnit === 1}
                                onChange={() => setSelectedUsageUnit(1)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                disabled={isSubmitting}
                            />
                            <span className="ml-2">Günlük Kullanım</span>
                        </label>
                        <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-200">
                            <input
                                type="radio"
                                name="usageUnit"
                                value="2"
                                checked={selectedUsageUnit === 2}
                                onChange={() => setSelectedUsageUnit(2)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                disabled={isSubmitting}
                            />
                            <span className="ml-2">Saatlik Kullanım</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Süre önizleme + negatif uyarı */}
            {(leaveDurationText || durationLoading || calculatedDurationData?.warningMessage) && (
                <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
                    <div className="flex items-center">
                        Talep edilen izin süresi:{' '}
                        {durationLoading ? (
                            <span className="ml-2 flex items-center text-blue-600">
                                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Hesaplanıyor...
                            </span>
                        ) : (
                            <strong className="ml-1">{leaveDurationText}</strong>
                        )}
                    </div>

                    {!durationLoading && calculatedDurationData?.warningMessage && (
                        <div className="mt-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3.5 py-2.5 text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-200 space-y-1">
                            <div className="font-bold flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span>İzin Süresi Hesaplama Uyarısı</span>
                            </div>
                            <p className="text-xs leading-relaxed">
                                {calculatedDurationData.warningMessage}
                            </p>
                        </div>
                    )}

                    {isBalanceTracked &&
                        hasRequestedUsage &&
                        canGoNegative &&
                        isUsingNegative && (
                            <div className="mt-1 rounded-md border border-purple-300 bg-purple-50 px-3 py-2 text-purple-800 dark:border-purple-500 dark:bg-purple-900/30 dark:text-purple-100">
                                Bu talep ile yıllık izin hakkını aşıyorsun.{' '}
                                {typeof kalanGun === 'number' && (
                                    <>
                                        Pozitif hakkın:{' '}
                                        <strong>
                                            {kalanGun.toLocaleString('tr-TR')} gün
                                        </strong>
                                        .{' '}
                                    </>
                                )}
                                Kalan kısım, tanımlı negatif bakiyeden kullanılacaktır.
                            </div>
                        )}

                    {isBalanceTracked &&
                        hasRequestedUsage &&
                        shouldBlockByBalance && (
                            <div className="mt-1 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-700 dark:border-red-500 dark:bg-red-900/30 dark:text-red-100">
                                Talep edilen süre, mevcut haklarınız ve bekleyen izin
                                taleplerinizle birlikte bu izin türü için tanımlı toplam
                                (pozitif + negatif) bakiye limitini aşıyor. Lütfen tarih veya
                                saat aralığını kısaltın.
                            </div>
                        )}
                </div>
            )}

            {/* Açıklama */}
            <div>
                <label
                    htmlFor="aciklama"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                >
                    Açıklama
                </label>
                <textarea
                    id="aciklama"
                    name="aciklama"
                    value={formData.aciklama}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    disabled={isSubmitting}
                    placeholder="Talebinizle ilgili kısa bir açıklama yazabilirsiniz..."
                />
            </div>

            {/* Admin/İK: Onaysız kaydet */}
            {canOverrideApproval && !initialData && (
                <div className="rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 p-4">
                    <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-indigo-600 dark:text-indigo-300 mt-0.5" />
                        <div className="flex-1">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={!!formData.onaysizKaydet}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            onaysizKaydet: e.target.checked
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    Onaysız kaydet (akışı bypass et)
                                </span>
                            </label>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                                Bu seçenek açıkken talep, onay akışına girmeden direkt <strong>Onaylandı</strong> olarak kaydedilir.
                                Kendi izniniz için kullanılamaz; kendi talebiniz yine akışa düşer.
                            </p>

                            {formData.onaysizKaydet && (
                                <div className="mt-3">
                                    <label
                                        htmlFor="onaysizKaydetNotu"
                                        className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1"
                                    >
                                        Not (opsiyonel)
                                    </label>
                                    <textarea
                                        id="onaysizKaydetNotu"
                                        value={formData.onaysizKaydetNotu || ''}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                onaysizKaydetNotu: e.target.value
                                            }))
                                        }
                                        rows={2}
                                        className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        disabled={isSubmitting}
                                        placeholder="Örn: İK toplu giriş / sistem düzeltme"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Butonlar */}
            <div className="flex flex-col items-end space-y-1 pt-2">
                <div className="flex space-x-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <XCircle className="w-5 h-5 mr-2" />
                        İptal
                    </button>
                    <button
                        type="submit"
                        disabled={
                            isSubmitting ||
                            durationLoading ||
                            (calculatedDurationData && calculatedDurationData.netMinutes <= 0) ||
                            (isBalanceTracked && balanceLoading) ||
                            shouldBlockByBalance
                        }
                        className="flex items-center px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                    >
                        {isSubmitting ? (
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                            <Save className="w-5 h-5 mr-2" />
                        )}
                        Kaydet
                    </button>
                </div>
                {shouldBlockByBalance && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                        Talep edilen izin süresi, bekleyen talepleriniz de dikkate alındığında
                        bu izin türü için tanımlı bakiye limitini aştığı için kaydedilemez.
                    </p>
                )}
            </div>
        </form>
    );
}
